import React from "react";
import {
  Grid,
  TextField,
  Typography,
  Paper,
  Divider,
  useTheme
} from "@material-ui/core";
import useCampaignCalculations from "../../../hooks/useCampaignCalculations";

/**
 * Componente compartido para pestaña de Intervalos
 * Fecha: 2026-01-08
 * Descripción: Componente reutilizable para configurar intervalos de envío
 * 
 * @param {string} type - Tipo de campaña: 'contacts' | 'groups'
 * @param {number} totalItems - Total de items (contactos o grupos)
 * @param {string} itemListName - Nombre de la lista (opcional, para mostrar en el panel)
 * @param {Object} values - Valores del formulario (Formik)
 * @param {Function} setFieldValue - Función para actualizar valores (Formik)
 * @param {Object} touched - Objeto touched de Formik
 * @param {Object} errors - Objeto errors de Formik
 * @param {boolean} disabled - Si los campos están deshabilitados
 * @param {Object} classes - Clases CSS del componente padre (opcional)
 */
const IntervalTab = ({
  type = 'contacts',
  totalItems = 0,
  itemListName = null,
  values,
  setFieldValue,
  touched = {},
  errors = {},
  disabled = false,
  classes = {}
}) => {
  const theme = useTheme();
  
  // Calcular tiempo estimado usando el hook
  const { calculatedTime } = useCampaignCalculations(
    type,
    totalItems,
    values.messageInterval || 45,
    values.longerIntervalAfter || 20,
    values.greaterInterval || 60
  );

  // Textos adaptativos según el tipo
  const itemLabel = type === 'groups' ? 'grupos' : 'contactos';
  const itemLabelCapital = type === 'groups' ? 'Grupos' : 'Contactos';

  return (
    <Grid xs={12} item>
      <Typography 
        variant="h6" 
        gutterBottom 
        style={{ color: theme.palette.primary.main, marginBottom: 16 }}
      >
        <span role="img" aria-label="Configuración">⚙️</span> Configuración de Intervalos
      </Typography>

      {/* Campos en UNA SOLA FILA */}
      <Grid spacing={2} container>
        {/* Tiempo entre mensajes */}
        <Grid xs={12} sm={4} item>
          <TextField
            label="Tiempo Inicial (segundos)"
            name="messageInterval"
            type="number"
            value={values.messageInterval || 45}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 45;
              setFieldValue('messageInterval', val);
            }}
            variant="outlined"
            margin="dense"
            fullWidth
            inputProps={{ min: 5, max: 300 }}
            className={classes.textField || ''}
            disabled={disabled}
            error={touched.messageInterval && Boolean(errors.messageInterval)}
            helperText={touched.messageInterval && errors.messageInterval}
          />
        </Grid>

        {/* Aplicar intervalo mayor después de X items */}
        <Grid xs={12} sm={4} item>
          <TextField
            label={`Luego de X ${itemLabel}`}
            name="longerIntervalAfter"
            type="number"
            value={values.longerIntervalAfter || 20}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 20;
              setFieldValue('longerIntervalAfter', val);
            }}
            variant="outlined"
            margin="dense"
            fullWidth
            inputProps={{ min: 1, max: 100 }}
            className={classes.textField || ''}
            disabled={disabled}
            error={touched.longerIntervalAfter && Boolean(errors.longerIntervalAfter)}
            helperText={touched.longerIntervalAfter && errors.longerIntervalAfter}
          />
        </Grid>

        {/* Intervalo mayor entre mensajes */}
        <Grid xs={12} sm={4} item>
          <TextField
            label="Nuevo tiempo de envio"
            name="greaterInterval"
            type="number"
            value={values.greaterInterval || 60}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 60;
              setFieldValue('greaterInterval', val);
            }}
            variant="outlined"
            margin="dense"
            fullWidth
            inputProps={{ min: 10, max: 600 }}
            className={classes.textField || ''}
            disabled={disabled}
            error={touched.greaterInterval && Boolean(errors.greaterInterval)}
            helperText={touched.greaterInterval && errors.greaterInterval}
          />
        </Grid>
      </Grid>

      {/* Panel de Cálculo Dinámico */}
      {totalItems && totalItems > 0 && calculatedTime && calculatedTime.segundos > 0 && (
        <Grid xs={12} item style={{ marginTop: 24 }}>
          <Paper style={{ padding: 20, backgroundColor: theme.palette.background.paper }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              style={{ color: theme.palette.primary.main, marginBottom: 16 }}
            >
              <span role="img" aria-label="Estadísticas">📊</span> TIEMPO DE ENVÍO ESTIMADO
            </Typography>
            
            {itemListName && (
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                <strong>Lista de {itemLabel}:</strong> {itemListName}
              </Typography>
            )}
            
            <Typography 
              variant="body2" 
              style={{ marginBottom: 16, color: '#d32f2f', fontWeight: 'bold' }}
            >
              <span role="img" aria-label="Importante">🔴</span> <strong>Contiene:</strong> {totalItems} {itemLabel}
            </Typography>
            
            <Divider style={{ margin: '16px 0' }} />
            
            <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 'bold' }}>
              Desglose Paso a Paso:
            </Typography>
            
            <Typography variant="body2" style={{ marginBottom: 4 }}>
              <strong>Paso 1:</strong> Primeros {values.longerIntervalAfter || 20} {itemLabel} × {values.messageInterval || 45} segundos = {Math.min(values.longerIntervalAfter || 20, totalItems) * (values.messageInterval || 45)} segundos
            </Typography>
            
            {totalItems > (values.longerIntervalAfter || 20) && (
              <>
                <Typography variant="body2" style={{ marginBottom: 4 }}>
                  <strong>Paso 2:</strong> {itemLabelCapital} restantes = {totalItems} - {values.longerIntervalAfter || 20} = {totalItems - (values.longerIntervalAfter || 20)} {itemLabel}
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 4 }}>
                  {totalItems - (values.longerIntervalAfter || 20)} {itemLabel} × {values.greaterInterval || 60} segundos = {Math.max(0, totalItems - (values.longerIntervalAfter || 20) - 1) * (values.greaterInterval || 60)} segundos
                </Typography>
              </>
            )}
            
            <Typography variant="body2" style={{ marginBottom: 4 }}>
              <strong>Paso 3:</strong> Tiempo total = {calculatedTime.segundos} segundos
            </Typography>
            
            <Typography variant="body2" style={{ marginBottom: 16 }}>
              <strong>Paso 4:</strong> {calculatedTime.segundos} segundos ÷ 60 = {calculatedTime.minutos} minutos
              {calculatedTime.horas > 0 && (
                <> = {calculatedTime.horas} hora{calculatedTime.horas > 1 ? 's' : ''} {calculatedTime.minutosRestantes} minuto{calculatedTime.minutosRestantes !== 1 ? 's' : ''}</>
              )}
            </Typography>
            
            <Divider style={{ margin: '16px 0' }} />
            
            <Typography variant="h6" style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
              <span role="img" aria-label="Resultado">✅</span> Resultado final:
            </Typography>
            <Typography variant="body1" style={{ fontWeight: 'bold', marginTop: 8 }}>
              Tiempo estimado: {calculatedTime.segundos} segundos = {calculatedTime.texto}
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default IntervalTab;
